import { useState } from 'react';
import { FormData } from '../types';
import { validateHexColor, getPersonalityText, validateEmail } from '../utils/helpers';
import { colorPalettes, logoExampleOptions, personalitySliders } from '../constants';

export const initialFormData: FormData = {
  businessName: '',
  industry: '',
  logoExamples: [],
  personalitySliders: {
    modernClassic: 50,
    playfulSerious: 50,
    luxuryEconomical: 50,
    minimalistDetailed: 50,
    boldSubtle: 50
  },
  colorPreferences: [],
  customColors: ['#000000', '#000000', '#000000', '#000000'],
  stylePreferences: [],
  additionalNotes: '',
  numCustomColors: 2,
  usingCustomColors: false,
  contactName: '',
  contactEmail: ''
};

export const useLogoForm = () => {
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [errors, setErrors] = useState<{ [key: number]: boolean }>({});
  const [duplicateColorError, setDuplicateColorError] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSliderChange = (sliderId: string, value: number) => {
    const snappedValue = value;
    setFormData(prev => ({
      ...prev,
      personalitySliders: {
        ...prev.personalitySliders,
        [sliderId]: snappedValue
      }
    }));
  };

  const handleCheckboxChange = (field: keyof FormData, value: string) => {
    setFormData(prev => {
      if (field === 'logoExamples') {
        const currentItems = prev[field];
        if (currentItems.includes(value)) {
          return {
            ...prev,
            [field]: currentItems.filter(item => item !== value)
          };
        } else if (currentItems.length < 5) {
          return {
            ...prev,
            [field]: [...currentItems, value]
          };
        }
        return prev;
      }

      if (field === 'stylePreferences') {
        const currentItems = prev[field];
        if (currentItems.includes(value)) {
          return {
            ...prev,
            [field]: currentItems.filter(item => item !== value)
          };
        } else if (currentItems.length < 3) {
          return {
            ...prev,
            [field]: [...currentItems, value]
          };
        }
        return prev;
      }

      return {
        ...prev,
        [field]: prev[field].includes(value)
          ? prev[field].filter(item => item !== value)
          : [...prev[field], value]
      };
    });
  };

  const handleColorPaletteSelect = (paletteName: string) => {
    setFormData(prev => ({
      ...prev,
      colorPreferences: [paletteName],
      usingCustomColors: false,
      customColors: ['#000000', '#000000', '#000000', '#000000'],
      numCustomColors: 2
    }));
    setDuplicateColorError(false);
  };

  const handleCustomColorChange = (index: number, color: string) => {
    if (!/^#[0-9A-Fa-f]{0,6}$/.test(color)) {
      return;
    }

    if (!color.startsWith('#')) {
      color = '#' + color;
    }

    const newColors = [...formData.customColors];
    newColors[index] = color.toUpperCase();

    setFormData(prev => ({
      ...prev,
      usingCustomColors: true,
      colorPreferences: [],
      customColors: newColors
    }));

    if (validateHexColor(color)) {
      setErrors(prevErrors => ({ ...prevErrors, [index]: false }));
      
      const validColors = newColors
        .slice(0, formData.numCustomColors)
        .filter((c, i) => validateHexColor(c));
      const uniqueColors = new Set(validColors);
      setDuplicateColorError(validColors.length !== uniqueColors.size);
    }
  };

  const addColor = () => {
    setFormData(prev => ({
      ...prev,
      numCustomColors: prev.numCustomColors + 1,
      usingCustomColors: true,
      colorPreferences: []
    }));
  };

  const removeLastColor = () => {
    setFormData(prev => ({
      ...prev,
      numCustomColors: prev.numCustomColors - 1,
      usingCustomColors: true,
      colorPreferences: []
    }));
    
    const validColors = formData.customColors
      .slice(0, formData.numCustomColors - 1)
      .filter(c => validateHexColor(c));
    const uniqueColors = new Set(validColors);
    setDuplicateColorError(validColors.length !== uniqueColors.size);
  };

  const validateStep = (currentStep: number): boolean => {
    switch (currentStep) {
      case 1:
        return (
          formData.contactName?.trim() !== '' && 
          formData.contactEmail?.trim() !== '' && 
          validateEmail(formData.contactEmail)
        );
      case 2:
        return formData.businessName.trim() !== '' && formData.industry.trim() !== '';
      case 3:
        return formData.logoExamples.length >= 1;
      case 4:
        return true;
      case 5:
        if (formData.usingCustomColors) {
          const validColors = formData.customColors
            .slice(0, formData.numCustomColors)
            .every(color => validateHexColor(color));
          
          if (validColors) {
            const uniqueColors = new Set(
              formData.customColors
                .slice(0, formData.numCustomColors)
                .filter(color => validateHexColor(color))
            );
            return uniqueColors.size === formData.numCustomColors && !duplicateColorError;
          }
          return false;
        }
        return formData.colorPreferences.length > 0;
      default:
        return true;
    }
  };

  const generatePrompt = () => {
    const selectedPalette = colorPalettes.find(p => formData.colorPreferences.includes(p.name));
    const colors = formData.usingCustomColors
      ? formData.customColors.slice(0, formData.numCustomColors)
      : selectedPalette?.colors || [];

    const logoExampleIds = formData.logoExamples.map(id => parseInt(id));

    const personalityDescriptions = personalitySliders.map(slider => {
      const value = formData.personalitySliders[slider.id];
      return getPersonalityText(value, slider.left, slider.right);
    });

    const today = new Date();
    const submissionDate = today.toISOString().split('T')[0];

    return {
      name: formData.contactName,
      email: formData.contactEmail,
      businessName: formData.businessName,
      industry: formData.industry,
      logoExamples: logoExampleIds.join(', '),
      personality: personalityDescriptions.join(', '),
      colors: colors.join(', '),
      additionalInformation: formData.additionalNotes,
      submissionDate: submissionDate
    };
  };

  const handleExport = () => {
    const prompt = generatePrompt();
    const blob = new Blob([JSON.stringify(prompt, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'logo-design-brief.json';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return {
    formData,
    errors,
    duplicateColorError,
    handleInputChange,
    handleSliderChange,
    handleCheckboxChange,
    handleColorPaletteSelect,
    handleCustomColorChange,
    addColor,
    removeLastColor,
    validateStep,
    handleExport,
    generatePrompt,
    setErrors
  };
};